import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { Plus, Trash2, Save, X, Briefcase } from "lucide-react";

const empty = {
  title: "", organisation: "", description: "",
  skills_required: [], industry: "", time_commitment: "", duration: "",
  status: "draft", opens_at: "", closes_at: "", published: false,
};

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60 mb-1">{label}</div>
    {children}
  </div>
);
const Input = (p) => <input {...p} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />;
const Textarea = (p) => <textarea rows={p.rows || 3} {...p} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />;
const Select = ({ options, ...rest }) => (
  <select {...rest} className="w-full px-3 py-2 rounded-md bg-white border text-[13px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const OppForm = ({ initial, onSubmit, onCancel, saving }) => {
  const [f, setF] = useState(initial ? { ...initial, skills_csv: (initial.skills_required || []).join(', ') } : { ...empty, skills_csv: '' });
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setF(s => ({ ...s, [k]: v }));
  };
  const submit = (e) => {
    e.preventDefault();
    const skills = (f.skills_csv || '').split(',').map(s => s.trim()).filter(Boolean);
    // eslint-disable-next-line no-unused-vars
    const { skills_csv, ...rest } = f;
    onSubmit({ ...rest, skills_required: skills });
  };
  return (
    <form onSubmit={submit} className="rounded-lg p-6 bg-white border" style={{ borderColor: 'rgba(13,42,32,0.08)' }} data-testid="opp-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[18px] font-medium">{initial?.id ? "Edit opportunity" : "New opportunity"}</h3>
        <button type="button" onClick={onCancel} className="text-[13px] link-underline inline-flex items-center gap-1"><X size={14} /> Cancel</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Title" full><Input required data-testid="opp-title" value={f.title} onChange={set('title')} /></Field>
        <Field label="Organisation"><Input value={f.organisation} onChange={set('organisation')} /></Field>
        <Field label="Industry"><Input value={f.industry} onChange={set('industry')} /></Field>
        <Field label="Time commitment"><Input value={f.time_commitment} onChange={set('time_commitment')} placeholder="e.g. 4 hrs / week" /></Field>
        <Field label="Duration"><Input value={f.duration} onChange={set('duration')} placeholder="e.g. 3 months" /></Field>
        <Field label="Description" full><Textarea rows={4} value={f.description} onChange={set('description')} /></Field>
        <Field label="Skills required (comma-separated)" full><Input value={f.skills_csv} onChange={set('skills_csv')} placeholder="Marketing, Product design, Fundraising" /></Field>
        <Field label="Opens at"><Input type="date" value={f.opens_at || ''} onChange={set('opens_at')} /></Field>
        <Field label="Closes at"><Input type="date" value={f.closes_at || ''} onChange={set('closes_at')} /></Field>
        <Field label="Status">
          <Select data-testid="opp-status" value={f.status} onChange={set('status')} options={[{value:'draft',label:'Draft'},{value:'open',label:'Open'},{value:'closed',label:'Closed'}]} />
        </Field>
        <Field label="Published">
          <label className="inline-flex items-center gap-2 text-[13px] mt-2"><input type="checkbox" data-testid="opp-published" checked={!!f.published} onChange={set('published')} /> Visible to members</label>
        </Field>
      </div>
      <div className="mt-6 flex items-center gap-2">
        <button type="submit" disabled={saving} data-testid="opp-save-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px] disabled:opacity-60"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md btn-ghost text-[13px]">Cancel</button>
      </div>
    </form>
  );
};

const CMSOpportunities = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminApi.get('/admin/opportunities'); setList(data); }
    catch { toast({ title: "Failed to load opportunities" }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    setSaving(true);
    try { await adminApi.post('/admin/opportunities', payload); toast({ title: "Opportunity created" }); setMode('list'); load(); }
    catch (err) { toast({ title: "Create failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setSaving(false); }
  };
  const update = async (payload) => {
    setSaving(true);
    try { await adminApi.patch(`/admin/opportunities/${editing.id}`, payload); toast({ title: "Updated" }); setMode('list'); setEditing(null); load(); }
    catch (err) { toast({ title: "Update failed", description: err?.response?.data?.detail || "Try again." }); }
    finally { setSaving(false); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.title}"?`)) return;
    try { await adminApi.delete(`/admin/opportunities/${o.id}`); toast({ title: "Deleted" }); load(); }
    catch (err) { toast({ title: "Delete failed", description: err?.response?.data?.detail || "Try again." }); }
  };

  if (mode === 'new') return <OppForm onSubmit={create} onCancel={() => setMode('list')} saving={saving} />;
  if (mode === 'edit') return <OppForm initial={editing} onSubmit={update} onCancel={() => { setMode('list'); setEditing(null); }} saving={saving} />;

  return (
    <div className="rounded-lg p-6" style={{ background: '#fbf8ef' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[20px] font-medium">Opportunities</h3>
        <button onClick={() => setMode('new')} data-testid="opp-new-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-coral text-[13px]"><Plus size={14} /> New opportunity</button>
      </div>
      {loading ? <div className="p-6 text-center text-[13px] text-[#0d2a20]/60">Loading…</div>
        : list.length === 0 ? <div className="p-6 text-center text-[13px] text-[#0d2a20]/60">No opportunities yet.</div>
        : (
          <div className="overflow-x-auto bg-white rounded-md">
            <table className="w-full">
              <thead className="text-[11px] uppercase tracking-wider text-[#0d2a20]/60" style={{ background: '#ece5d3' }}>
                <tr>
                  <th className="text-left py-2 px-3">Title</th>
                  <th className="text-left py-2 px-3">Organisation</th>
                  <th className="text-left py-2 px-3">Industry</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Published</th>
                  <th className="text-left py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(o => (
                  <tr key={o.id} className="border-b last:border-b-0 hover:bg-[#f6f0dc]/60" style={{ borderColor: 'rgba(13,42,32,0.06)' }}>
                    <td className="py-2 px-3 text-[13px] font-medium"><Briefcase size={12} className="inline mr-1 text-[#0d2a20]/50" />{o.title}</td>
                    <td className="py-2 px-3 text-[12px] text-[#0d2a20]/70">{o.organisation || '—'}</td>
                    <td className="py-2 px-3 text-[12px] text-[#0d2a20]/70">{o.industry || '—'}</td>
                    <td className="py-2 px-3 text-[12px]"><span className="px-2 py-1 rounded" style={{ background: o.status==='open' ? '#d6ecd6' : o.status==='closed' ? '#f3d6d1' : '#fde5c7' }}>{o.status}</span></td>
                    <td className="py-2 px-3 text-[12px]"><span className="px-2 py-1 rounded" style={{ background: o.published ? '#d6ecd6' : '#ece5d3' }}>{o.published ? 'Yes' : 'No'}</span></td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditing(o); setMode('edit'); }} data-testid={`opp-edit-${o.id}`} className="px-2 py-1 rounded btn-ghost text-[11px]">Edit</button>
                        <button onClick={() => remove(o)} className="p-1.5 rounded hover:bg-[#f3d6d1] text-[#7a1f13]" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
};

export default CMSOpportunities;
